import { notification } from '@surecompanies/core_components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import epsClientV1 from 'enrollment-platform/api/eps-v1.client'
import { useState } from 'react'

export interface CsvRowError {
  row: number
  field: string
  message: string
}

interface UploadParams {
  file: File
  title: string
  description?: string
}

interface UploadResponse {
  metadataId: number
  rowCount: number
}

interface ErrorResponseData {
  error: string
  message?: string
  row_errors?: CsvRowError[]
}

async function uploadBenchmarkSnapshot(
  params: UploadParams
): Promise<UploadResponse> {
  const formData = new FormData()
  formData.append('file', params.file)
  formData.append('title', params.title)
  if (params.description) {
    formData.append('description', params.description)
  }
  // Do NOT set Content-Type — let axios/browser auto-populate with multipart boundary
  const { data } = await epsClientV1.post<UploadResponse>(
    '/benchmark-plans',
    formData
  )
  return data
}

export function useUploadBenchmarkSnapshot() {
  const queryClient = useQueryClient()
  const [rowErrors, setRowErrors] = useState<CsvRowError[]>([])

  const mutation = useMutation<UploadResponse, Error, UploadParams>({
    mutationFn: uploadBenchmarkSnapshot,
    onMutate: () => {
      setRowErrors([])
    },
    onSuccess: (data) => {
      notification.success({
        message: 'Snapshot uploaded successfully',
        description: `${data.rowCount} rows imported`,
      })
      void queryClient.invalidateQueries({ queryKey: ['benchmarkSnapshots'] })
      void queryClient.invalidateQueries({ queryKey: ['getBenchmarkPlans'] })
      setRowErrors([])
    },
    onError: (error) => {
      const axiosError = error as unknown as {
        response?: { data?: ErrorResponseData }
      }
      const data = axiosError.response?.data
      const rowErrors = data?.row_errors ?? []

      if (rowErrors.length > 0) {
        setRowErrors(rowErrors)
        return
      }

      setRowErrors([])
      notification.error({
        message: 'Upload failed',
        description:
          data?.message ?? 'An unexpected error occurred. Please try again.',
      })
    },
  })

  const reset = () => {
    mutation.reset()
    setRowErrors([])
  }

  return {
    mutate: mutation.mutate,
    isPending: mutation.isPending,
    rowErrors,
    reset,
  }
}
